import { UserClient, type UserLanguageUpdate_Command, type UserLanguageQuery_QueryResult } from 'shared/clients/client-UserGroup';
import type { Profile } from 'shared/src/modules/authModule/services/Authorization.contracts';
import { getLanguageSubtagFromBCP47LanguageTag } from 'shared/src/modules/languageModule/helpers/language.helpers';
import { useLanguage } from 'shared/src/composables/useLanguage.composable';
import { useMessages } from 'shared/src/composables/messages/useMessages.composable';
import { DESTINATION_APP_TYPE, MESSAGE_TYPE } from 'shared/src/composables/messages/messages.model';
import { INITIAL_LANGUAGE, CBI_INITIAL_LANGUAGE } from 'shared/src/modules/languageModule/consts';

import { axiosInstance } from '../apiConfig';
import { DATAHUB_API } from '../urlConfig';

const userClient: UserClient = new UserClient(DATAHUB_API, axiosInstance);
const { storeLanguage } = useLanguage();
const { startMessageListener, addOnMessageActionAlt } = useMessages();

startMessageListener();

addOnMessageActionAlt(
  DESTINATION_APP_TYPE.HOST,
  MESSAGE_TYPE.LANGUAGE_INJECTION,
  async ({ language, omitUpdateUserLang }) => {
    await setLangToDHAndFE(language, omitUpdateUserLang);
  }
);

addOnMessageActionAlt(
  DESTINATION_APP_TYPE.HOST,
  MESSAGE_TYPE.MOBILE_LANGUAGE_INJECTION,
  async (lang: string) => storeLanguage(lang)
);

export const setCbiLang = async (profile: Profile): Promise<void> => {
  if (profile.slng) {
    storeLanguage(getLanguageSubtagFromBCP47LanguageTag(profile.slng));
  } else {
    storeLanguage(CBI_INITIAL_LANGUAGE);
  }
};

export const updateUserLanguage = async (userLanguage: string): Promise<string | undefined> => {
  try {
    const userLangCommand: UserLanguageUpdate_Command = {
      language: userLanguage,
    };

    await userClient.setLanguage(userLangCommand);

    return userLanguage;
  } catch (error) {
    console.warn(error);
  }
};

const getUserLanguage = async (): Promise<UserLanguageQuery_QueryResult | undefined> => {
  try {
    return await userClient.getLanguage();
  } catch (error) {
    console.warn(error);
  }
};

const setLangToDHAndFE = async (lang: string, omitUpdateUserLang?: boolean): Promise<void> => {
  const langTag = getLanguageSubtagFromBCP47LanguageTag(lang);
  if (!omitUpdateUserLang) {
    await updateUserLanguage(langTag);
  }
  storeLanguage(langTag);
};

export const setLanguage = async (profile: Profile): Promise<void> => {
  const userLanguage = await getUserLanguage();
  if (userLanguage?.language) {
    storeLanguage(userLanguage?.language);
  } else if (profile.slng) {
    await setLangToDHAndFE(profile.slng);
  } else {
    storeLanguage(INITIAL_LANGUAGE);
  }
};
